import React, { useMemo, useState } from 'react';
import type { SimNPC } from '../../simulation/types/SimNPC';
import type { GroupConfig } from '../../simulation/types/Group';

interface NPCDetailPanelProps {
  npc: SimNPC;
  allNPCs: SimNPC[];
  onClose: () => void;
  onNPCClick?: (npc: SimNPC) => void;
  isMobile?: boolean;
  groupConfig?: GroupConfig; // NPCが所属するグループの設定
}

type TabType = 'info' | 'deck';

export const NPCDetailPanel: React.FC<NPCDetailPanelProps> = ({
  npc,
  allNPCs,
  onClose,
  onNPCClick,
  isMobile = false,
  groupConfig,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('info');

  // パートナー情報
  const partnerRelation = npc.family.find((f) => f.relationType === 'partner');
  const partner = partnerRelation
    ? allNPCs.find((n) => n.id === partnerRelation.relationId)
    : null;

  // 親情報
  const parentRelations = npc.family.filter((f) => f.relationType === 'parent');
  const parents = parentRelations
    .map((rel) => allNPCs.find((n) => n.id === rel.relationId))
    .filter((p) => p !== undefined);

  // 子供情報
  const childRelations = npc.family.filter((f) => f.relationType === 'child');
  const children = childRelations
    .map((rel) => allNPCs.find((n) => n.id === rel.relationId))
    .filter((c) => c !== undefined);

  const getContainerStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.container,
        width: '100%',
        minWidth: 'unset',
        maxHeight: 'none',
        padding: '12px',
      };
    }
    return styles.container;
  };

  return (
    <div style={getContainerStyle()}>
      <div style={styles.header}>
        <h3 style={isMobile ? { ...styles.title, fontSize: '16px' } : styles.title}>
          {npc.name}
          {groupConfig && (
            <span style={{
              ...styles.groupBadge,
              backgroundColor: groupConfig.color,
            }}>
              {groupConfig.id}
            </span>
          )}
          {!npc.isAlive && ' 💀'}
        </h3>
        <button onClick={onClose} style={styles.closeButton}>
          ✕
        </button>
      </div>

      {/* タブバー */}
      <div style={styles.tabBar}>
        <button
          style={{
            ...styles.tabButton,
            ...(activeTab === 'info' ? styles.tabButtonActive : {}),
          }}
          onClick={() => setActiveTab('info')}
        >
          📋 情報
        </button>
        <button
          style={{
            ...styles.tabButton,
            ...(activeTab === 'deck' ? styles.tabButtonActive : {}),
          }}
          onClick={() => setActiveTab('deck')}
        >
          🎴 デッキ
        </button>
      </div>

      {/* タブコンテンツ */}
      {activeTab === 'info' && (
        <InfoTab npc={npc} partner={partner} parents={parents} children={children} onNPCClick={onNPCClick} isMobile={isMobile} />
      )}
      {activeTab === 'deck' && <DeckTab npc={npc} isMobile={isMobile} />}
    </div>
  );
};

/**
 * 情報タブ
 */
interface InfoTabProps {
  npc: SimNPC;
  partner: SimNPC | null | undefined;
  parents: (SimNPC | undefined)[];
  children: (SimNPC | undefined)[];
  onNPCClick?: (npc: SimNPC) => void;
  isMobile?: boolean;
}

const InfoTab: React.FC<InfoTabProps> = ({ npc, partner, parents, children, onNPCClick, isMobile = false }) => {
  return (
    <div style={styles.tabContent}>
      <div style={styles.status}>
        <div style={styles.statusItem}>
          <span style={styles.label}>体力:</span>
          <span style={styles.value}>{npc.health}/100</span>
        </div>
        <div style={styles.statusItem}>
          <span style={styles.label}>Rating:</span>
          <span style={styles.value}>{npc.rating}</span>
        </div>
      </div>

      <div style={styles.stats}>
        <div style={styles.statRow}>
          <span>戦績:</span>
          <span>
            {npc.wins}勝 {npc.losses}敗
          </span>
        </div>
        <div style={styles.statRow}>
          <span>生存日数:</span>
          <span>{npc.daysLived}日</span>
        </div>
        <div style={styles.statRow}>
          <span>生まれた日:</span>
          <span>{npc.birthDay === 0 ? '初期NPC' : `Day ${npc.birthDay}`}</span>
        </div>
        {!npc.isAlive && (
          <div style={styles.statRow}>
            <span>死亡日:</span>
            <span>Day {npc.birthDay + npc.daysLived}</span>
          </div>
        )}
        <div style={styles.statRow}>
          <span>世代:</span>
          <span>第{npc.generation || 1}世代</span>
        </div>
        <div style={styles.statRow}>
          <span>移動タイプ:</span>
          <span>{getMovementTypeJP(npc.movementType)}</span>
        </div>
      </div>

      {parents.length > 0 && (
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>👨‍👩 両親</h4>
          {parents.map((parent) =>
            parent ? (
              <div
                key={parent.id}
                style={{
                  ...styles.relationItem,
                  ...(onNPCClick ? styles.relationItemClickable : {}),
                }}
                onClick={() => onNPCClick?.(parent)}
              >
                {parent.name}
                {!parent.isAlive && ' 💀'}
              </div>
            ) : null
          )}
        </div>
      )}

      {partner && (
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>💑 パートナー</h4>
          <div
            style={{
              ...styles.relationItem,
              ...(onNPCClick ? styles.relationItemClickable : {}),
            }}
            onClick={() => onNPCClick?.(partner)}
          >
            {partner.name}
            {!partner.isAlive && ' 💀'}
          </div>
        </div>
      )}

      {children.length > 0 && (
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>👶 子供 ({children.length}人)</h4>
          {children.map((c) =>
            c ? (
              <div
                key={c.id}
                style={{
                  ...styles.relationItem,
                  ...(onNPCClick ? styles.relationItemClickable : {}),
                }}
                onClick={() => onNPCClick?.(c)}
              >
                {c.name}
                {!c.isAlive && ' 💀'}
              </div>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

/**
 * デッキタブ
 */
interface DeckTabProps {
  npc: SimNPC;
  isMobile?: boolean;
}

const DeckTab: React.FC<DeckTabProps> = ({ npc, isMobile = false }) => {
  const [showExportModal, setShowExportModal] = useState(false);
  const [copyResult, setCopyResult] = useState<string | null>(null);

  // デッキ統計を計算
  const deckStats = useMemo(() => {
    const cardCounts = new Map<string, { card: any; count: number; winRate: number; games: number; gamesInDeck: number }>();
    const attributeCounts = new Map<string, number>();
    const typeCounts = new Map<string, number>();

    npc.deck.forEach((card) => {
      // カード別集計
      const existing = cardCounts.get(card.id);
      if (existing) {
        existing.count++;
      } else {
        // カード統計から勝率を取得
        const stat = npc.cardStats?.get(card.id);
        const games = stat?.gamesWithCard || 0;
        const wins = stat?.winsWithCard || 0;
        const gamesInDeck = stat?.gamesInDeck || 0;
        const winRate = games >= 3 ? (wins / games) * 100 : -1; // 3戦未満は-1
        cardCounts.set(card.id, { card, count: 1, winRate, games, gamesInDeck });
      }

      // 属性別集計
      const attr = card.attribute || 'なし';
      attributeCounts.set(attr, (attributeCounts.get(attr) || 0) + 1);

      // タイプ別集計
      const type = card.type || 'unknown';
      typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
    });

    // 勝率順でソート（統計なしは末尾）
    const sortedCards = Array.from(cardCounts.values()).sort((a, b) => {
      if (a.winRate === -1 && b.winRate === -1) return b.count - a.count;
      if (a.winRate === -1) return 1;
      if (b.winRate === -1) return -1;
      return b.winRate - a.winRate;
    });

    return {
      cardCounts: sortedCards,
      attributeCounts: Array.from(attributeCounts.entries()).sort((a, b) => b[1] - a[1]),
      typeCounts: Array.from(typeCounts.entries()).sort((a, b) => b[1] - a[1]),
      totalCards: npc.deck.length,
    };
  }, [npc.deck, npc.cardStats]);

  // エクスポート: デッキをカードIDリストに変換
  const exportDeck = (): string => {
    const cardIds = npc.deck.map((card) => card.id);
    return cardIds.join(',');
  };

  // クリップボードにコピー
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(exportDeck());
      setCopyResult('コピーしました!');
      setTimeout(() => setCopyResult(null), 2000);
    } catch (err) {
      setCopyResult('コピーに失敗しました');
      setTimeout(() => setCopyResult(null), 2000);
    }
  };

  return (
    <div style={styles.tabContent}>
      <div style={styles.deckSummary}>
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>総枚数:</span>
          <span style={styles.summaryValue}>{deckStats.totalCards}</span>
        </div>
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>種類数:</span>
          <span style={styles.summaryValue}>{deckStats.cardCounts.length}</span>
        </div>
        <button
          onClick={() => setShowExportModal(true)}
          style={styles.exportButton}
        >
          エクスポート
        </button>
      </div>

      {/* 属性別内訳 */}
      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>🌈 属性別内訳</h4>
        <div style={styles.breakdownGrid}>
          {deckStats.attributeCounts.map(([attr, count]) => (
            <div key={attr} style={styles.breakdownItem}>
              <span style={styles.breakdownLabel}>{attr}:</span>
              <span style={styles.breakdownValue}>{count}枚</span>
            </div>
          ))}
        </div>
      </div>

      {/* タイプ別内訳 */}
      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>📇 タイプ別内訳</h4>
        <div style={styles.breakdownGrid}>
          {deckStats.typeCounts.map(([type, count]) => (
            <div key={type} style={styles.breakdownItem}>
              <span style={styles.breakdownLabel}>{getTypeJP(type)}:</span>
              <span style={styles.breakdownValue}>{count}枚</span>
            </div>
          ))}
        </div>
      </div>

      {/* カード一覧 */}
      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>🎴 カード一覧（勝率順）</h4>
        <div style={styles.cardList}>
          {deckStats.cardCounts.map((entry, index) => (
            <div key={`${entry.card.id}-${index}`} style={styles.cardItem}>
              <span style={styles.cardCount}>×{entry.count}</span>
              <div style={styles.cardInfo}>
                <span style={styles.cardName}>{entry.card.name}</span>
                <span style={styles.cardMeta}>
                  {entry.card.attribute || 'なし'} / {getTypeJP(entry.card.type)} / コスト
                  {entry.card.cost}
                </span>
              </div>
              {/* 勝率表示 */}
              <span
                style={{
                  ...styles.cardWinRate,
                  color:
                    entry.winRate === -1
                      ? '#666'
                      : entry.winRate >= 50
                      ? '#4ade80'
                      : entry.winRate >= 30
                      ? '#facc15'
                      : '#ef4444',
                }}
              >
                {entry.winRate === -1
                  ? `${entry.games}/${entry.gamesInDeck}戦`
                  : `${entry.winRate.toFixed(0)}%`}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* エクスポートモーダル */}
      {showExportModal && (
        <div
          style={styles.modalOverlay}
          onClick={() => setShowExportModal(false)}
        >
          <div
            style={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>デッキをエクスポート</h3>
              <button
                onClick={() => setShowExportModal(false)}
                style={styles.modalCloseButton}
              >
                ×
              </button>
            </div>
            <p style={styles.modalDescription}>
              デッキのカードIDリストをコピーして共有できます。
            </p>
            <textarea
              readOnly
              value={exportDeck()}
              style={styles.exportTextarea}
            />
            <div style={styles.modalButtons}>
              <button onClick={copyToClipboard} style={styles.copyButton}>
                コピー
              </button>
              <button
                onClick={() => setShowExportModal(false)}
                style={styles.modalCancelButton}
              >
                閉じる
              </button>
            </div>
            {copyResult && (
              <div style={styles.copyResultMessage}>{copyResult}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function getMovementTypeJP(type: string): string {
  const names: Record<string, string> = {
    random: 'ランダム',
    explorer: '探索者',
    social: '社交的',
    avoider: '回避型',
    // 属性シーカー
    fire_seeker: '炎の求道者',
    water_seeker: '水の求道者',
    light_seeker: '光の求道者',
    dark_seeker: '闘の求道者',
    primitive_seeker: '原始の求道者',
    future_seeker: '未来の求道者',
  };
  return names[type] || type;
}

function getTypeJP(type: string): string {
  const names: Record<string, string> = {
    monster: 'モンスター',
    magic: '魔法',
    field: 'フィールド',
    phase: 'フェイズ',
  };
  return names[type] || type;
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    background: '#1a1a2e',
    borderRadius: '12px',
    padding: '16px',
    border: '2px solid #444',
    width: '320px',
    minWidth: '320px',
    maxHeight: '600px',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  groupBadge: {
    fontSize: '12px',
    padding: '2px 8px',
    borderRadius: '10px',
    color: '#fff',
    fontWeight: 'bold',
    textShadow: '0 0 2px rgba(0,0,0,0.5)',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#999',
  },
  tabBar: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
    borderBottom: '1px solid #444',
    paddingBottom: '8px',
  },
  tabButton: {
    flex: 1,
    padding: '8px 12px',
    fontSize: '13px',
    cursor: 'pointer',
    border: 'none',
    borderRadius: '6px 6px 0 0',
    background: '#2a2a3e',
    color: '#aaa',
    fontWeight: 'bold',
    transition: 'all 0.2s',
  },
  tabButtonActive: {
    background: '#3a3a5e',
    color: '#fff',
    borderBottom: '2px solid #667eea',
  },
  tabContent: {
    flex: 1,
    overflowY: 'auto',
  },
  status: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px',
    padding: '10px',
    background: '#16162a',
    borderRadius: '6px',
  },
  statusItem: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
  },
  label: {
    fontWeight: 'bold',
    color: '#888',
  },
  value: {
    color: '#ddd',
  },
  stats: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '12px',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: '#ddd',
  },
  section: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #2a2a3e',
  },
  sectionTitle: {
    margin: '0 0 8px 0',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#aaa',
  },
  relationItem: {
    padding: '6px 10px',
    background: '#16162a',
    borderRadius: '4px',
    fontSize: '13px',
    marginBottom: '4px',
    color: '#ddd',
  },
  relationItemClickable: {
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s',
  },
  deckSummary: {
    display: 'flex',
    gap: '16px',
    marginBottom: '12px',
    padding: '10px',
    background: '#16162a',
    borderRadius: '6px',
  },
  summaryItem: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: '13px',
    color: '#888',
  },
  summaryValue: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#60a5fa',
  },
  breakdownGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '6px',
  },
  breakdownItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 8px',
    background: '#16162a',
    borderRadius: '4px',
    fontSize: '13px',
  },
  breakdownLabel: {
    color: '#888',
  },
  breakdownValue: {
    color: '#ddd',
    fontWeight: 'bold',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  cardItem: {
    display: 'flex',
    gap: '10px',
    padding: '6px 10px',
    background: '#16162a',
    borderRadius: '4px',
    alignItems: 'center',
  },
  cardCount: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#60a5fa',
    minWidth: '30px',
  },
  cardInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  cardName: {
    fontSize: '13px',
    color: '#ddd',
    fontWeight: 'bold',
  },
  cardMeta: {
    fontSize: '11px',
    color: '#888',
  },
  cardWinRate: {
    fontSize: '13px',
    fontWeight: 'bold',
    minWidth: '40px',
    textAlign: 'right',
  },
  // エクスポート関連
  exportButton: {
    marginLeft: 'auto',
    padding: '4px 12px',
    fontSize: '11px',
    cursor: 'pointer',
    border: '1px solid rgba(78,205,196,0.5)',
    borderRadius: '4px',
    background: 'rgba(78,205,196,0.2)',
    color: '#4ecdc4',
    fontWeight: 'bold',
    transition: 'all 0.2s',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  modalContent: {
    background: 'linear-gradient(135deg, #1a1a3a 0%, #2a2a4a 100%)',
    borderRadius: '12px',
    padding: '20px',
    border: '2px solid #667eea',
    maxWidth: '400px',
    width: '90%',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  modalTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#4ecdc4',
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    color: '#999',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '0 8px',
  },
  modalDescription: {
    fontSize: '12px',
    color: '#aaa',
    marginBottom: '12px',
  },
  exportTextarea: {
    width: '100%',
    height: '120px',
    padding: '10px',
    borderRadius: '6px',
    border: '1px solid #444',
    background: '#0a0a1a',
    color: '#e0e0e0',
    fontSize: '11px',
    fontFamily: 'monospace',
    resize: 'none',
    boxSizing: 'border-box',
  },
  modalButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
  },
  copyButton: {
    flex: 1,
    padding: '10px',
    borderRadius: '6px',
    border: 'none',
    background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '13px',
    cursor: 'pointer',
  },
  modalCancelButton: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: '1px solid #444',
    background: 'rgba(100,100,100,0.3)',
    color: '#aaa',
    fontWeight: 'bold',
    fontSize: '13px',
    cursor: 'pointer',
  },
  copyResultMessage: {
    marginTop: '10px',
    padding: '8px',
    borderRadius: '4px',
    background: 'rgba(78,205,196,0.2)',
    border: '1px solid rgba(78,205,196,0.5)',
    color: '#4ecdc4',
    fontSize: '12px',
    textAlign: 'center',
  },
};
